// MissionBotService.js

import { functions, httpsCallable } from 'firebaseConfig';

const executeMissionBotSendMessageCallable = httpsCallable(functions, 'executeMissionBotSendMessage');
const executeMissionBotTaskCallable = httpsCallable(functions, 'executeMissionBotTask');

/**
 * executeMissionBotSendMessage
 *
 * Sends a mission bot email to the configured workspace-member recipients.
 *
 * @param {object} params - Mission email delivery inputs.
 * @param {string} params.idempotencyKeyPrefix - Stable idempotency prefix for the task execution.
 * @param {string[]} params.recipientEmails - Raw email recipients resolved from the node.
 * @param {string[]} params.recipientUserKeys - Workspace member user keys.
 * @param {string} params.subject - Email subject.
 * @param {string} params.text - Plain-text email body.
 * @param {string} params.baseKey - Workspace scope used to resolve recipients.
 *
 * @returns {Promise<object|null>} Delivery result payload.
 */
export async function executeMissionBotSendMessage({
  idempotencyKeyPrefix,
  recipientEmails,
  recipientUserKeys,
  subject,
  text,
  baseKey,
} = {}) {
  const { data } = await executeMissionBotSendMessageCallable({
    baseKey,
    idempotencyKeyPrefix,
    recipientEmails,
    recipientUserKeys,
    subject,
    text,
    workspaceKey: baseKey,
  });

  return data || null;
}

/**
 * executeMissionBotTask
 *
 * Requests backend-owned execution for a node-scoped Mission Bot task.
 *
 * @param {object} params - Mission task execution inputs.
 * @param {string} params.botJob - Mission Bot job key.
 * @param {string} params.nodeKey - Target node key.
 * @param {string[]} params.recipientEmails - Raw email recipients resolved from the node.
 * @param {string[]} params.recipientUserKeys - Workspace member user keys.
 * @param {string} params.subject - Email subject.
 * @param {string} params.taskKey - Target Mission task key.
 * @param {string} params.templateKey - Target Mission template key.
 * @param {string} params.text - Plain-text email body.
 * @param {string} params.baseKey - Workspace scope for authorization and delivery.
 *
 * @returns {Promise<object|null>} Execution result payload.
 */
export async function executeMissionBotTask({
  botJob,
  nodeKey,
  recipientEmails,
  recipientUserKeys,
  subject,
  taskKey,
  templateKey,
  text,
  baseKey,
} = {}) {
  const { data } = await executeMissionBotTaskCallable({
    baseKey,
    botJob,
    nodeKey,
    recipientEmails,
    recipientUserKeys,
    subject,
    taskKey,
    templateKey,
    text,
    workspaceKey: baseKey,
  });

  return data || null;
}